import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { ansCasesHref } from '@/pages/ans/OverviewPage';
import { fetchAnsAppeals } from '@/services/ans';
import type { AnsGroupTable, AnswerOut } from '@/services/types';

/**
 * What the Diretoria Colegiada did with a first-instance fine, by group.
 *
 * ANS has one reviewing instance and it reviews under half of the corpus,
 * so every share on this page is over REVIEWED cases and the corpus
 * denominator is printed beside it. Groups are descriptive, alphabetical,
 * and suppressed below the floor rather than shown at 0% or 100%.
 */
export function AnsAppealsPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'appeals'],
    queryFn: fetchAnsAppeals,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:appeals.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:appeals.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:appeals.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/appeals" label={t('ans:appeals.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{ ...data.outcomes, value: data.outcomes.value?.outcomes ?? null }}
          titleKey="ans:appeals.outcomes.title"
          helpKey="ans:appeals.outcomes.help"
          labelPrefix="ans:overview.outcomes."
          rowHref={(code) => ansCasesHref({ board_outcome: code })}
        />

        <section className="rounded-lg border bg-card p-4" data-relief>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ans:appeals.relief.title')}
          </h2>
          <Answer answer={data.relief} help={t('ans:appeals.relief.help')}>
            {(r) => (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Figure
                    label={t('ans:appeals.relief.originalTotal')}
                    value={currency.format(r.original_total)}
                  />
                  <Figure
                    label={t('ans:appeals.relief.finalTotal')}
                    value={currency.format(r.final_total)}
                  />
                  <Figure
                    label={t('ans:appeals.relief.reliefTotal')}
                    value={`${currency.format(r.relief_total)} (${percent.format(
                      r.relief_share_of_original,
                    )})`}
                  />
                  <Figure
                    label={t('ans:appeals.relief.medianRetained')}
                    value={
                      r.median_retained_when_reduced === null
                        ? '—'
                        : percent.format(r.median_retained_when_reduced)
                    }
                  />
                </div>
                {data.outcomes.value ? (
                  <p
                    className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground"
                    data-both-denominators
                  >
                    {t('ans:appeals.relief.bothDenominators', {
                      relief: number.format(data.outcomes.value.relief),
                      reviewed: number.format(data.outcomes.value.reviewed),
                      ofReviewed: percent.format(data.outcomes.value.relief_share_of_reviewed),
                      total: number.format(data.outcomes.value.total_cases),
                      ofAll: percent.format(data.outcomes.value.relief_share_of_all),
                      underAppeal: number.format(data.outcomes.value.under_appeal),
                    })}
                  </p>
                ) : null}
              </div>
            )}
          </Answer>
        </section>
      </div>

      <FieldGuide i18nKey="ans:appeals.guide" level={2} />

      <GroupTablePanel answer={data.by_track} />
      <GroupTablePanel answer={data.by_object} />
      <GroupTablePanel answer={data.by_year} />
    </div>
  );
}

function GroupTablePanel({ answer }: { answer: AnswerOut<AnsGroupTable> }) {
  const { t, i18n } = useTranslation(['ans']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const kind = answer.value?.kind ?? 'track';
  const href = (group: string) =>
    kind === 'track'
      ? ansCasesHref({ track: group, board_outcome: 'any' })
      : kind === 'object'
        ? ansCasesHref({ object: group, board_outcome: 'any' })
        : ansCasesHref({ year: Number(group), board_outcome: 'any' });

  return (
    <section className="rounded-lg border bg-card p-4" data-group-table={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ans:appeals.groups.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ans:appeals.groups.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t(`ans:appeals.groups.${kind}.group`)}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:appeals.groups.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:appeals.groups.reviewed')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:appeals.groups.maintained')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:appeals.groups.relief')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:appeals.groups.notAdmitted')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:appeals.groups.reduced')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.group} className="border-t" data-group={row.group}>
                      <td className="px-3 py-2 text-foreground">{row.group}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.cases)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={href(row.group)}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(row.reviewed)}
                        </Link>
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.reviewed_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.maintained)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.relief)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.relief_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.not_admitted)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reduced)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('ans:appeals.groups.suppressed', {
                  groups: number.format(table.suppressed),
                  cases: number.format(table.suppressed_reviewed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
